/**
 * AxLabs Simulation Core - Vector Mathematics
 *
 * Immutable 2D and 3D vector operations for physics calculations.
 * All operations return new vectors to maintain immutability.
 */
// ============================================================================
// 2D VECTOR OPERATIONS
// ============================================================================
/**
 * Create a new 2D vector
 */
export function vec2(x, y) {
    return { x, y };
}
/**
 * Zero vector
 */
export const VEC2_ZERO = { x: 0, y: 0 };
/**
 * Unit vectors
 */
export const VEC2_UNIT_X = { x: 1, y: 0 };
export const VEC2_UNIT_Y = { x: 0, y: 1 };
/**
 * Add two vectors
 */
export function add2(a, b) {
    return { x: a.x + b.x, y: a.y + b.y };
}
/**
 * Subtract vectors (a - b)
 */
export function sub2(a, b) {
    return { x: a.x - b.x, y: a.y - b.y };
}
/**
 * Multiply vector by scalar
 */
export function scale2(v, s) {
    return { x: v.x * s, y: v.y * s };
}
/**
 * Divide vector by scalar
 */
export function div2(v, s) {
    if (s === 0) {
        return { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY };
    }
    return { x: v.x / s, y: v.y / s };
}
/**
 * Dot product
 */
export function dot2(a, b) {
    return a.x * b.x + a.y * b.y;
}
/**
 * Cross product (returns scalar for 2D - the z component)
 */
export function cross2(a, b) {
    return a.x * b.y - a.y * b.x;
}
/**
 * Vector magnitude (length)
 */
export function magnitude2(v) {
    return Math.sqrt(v.x * v.x + v.y * v.y);
}
/**
 * Squared magnitude (faster, avoids sqrt)
 */
export function magnitudeSquared2(v) {
    return v.x * v.x + v.y * v.y;
}
/**
 * Normalize vector to unit length
 */
export function normalize2(v) {
    const mag = magnitude2(v);
    if (mag === 0) {
        return VEC2_ZERO;
    }
    return { x: v.x / mag, y: v.y / mag };
}
/**
 * Negate vector
 */
export function negate2(v) {
    return { x: -v.x, y: -v.y };
}
/**
 * Rotate vector by angle (radians)
 */
export function rotate2(v, angle) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
        x: v.x * cos - v.y * sin,
        y: v.x * sin + v.y * cos,
    };
}
/**
 * Perpendicular vector (90 degrees counterclockwise)
 */
export function perp2(v) {
    return { x: -v.y, y: v.x };
}
/**
 * Linear interpolation between two vectors
 */
export function lerp2(a, b, t) {
    return {
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
    };
}
/**
 * Distance between two points
 */
export function distance2(a, b) {
    return magnitude2(sub2(b, a));
}
/**
 * Squared distance (faster, avoids sqrt)
 */
export function distanceSquared2(a, b) {
    return magnitudeSquared2(sub2(b, a));
}
/**
 * Angle of vector from positive x-axis (radians, -π to π)
 */
export function angle2(v) {
    return Math.atan2(v.y, v.x);
}
/**
 * Angle between two vectors (radians)
 */
export function angleBetween2(a, b) {
    const dot = dot2(a, b);
    const magA = magnitude2(a);
    const magB = magnitude2(b);
    if (magA === 0 || magB === 0) {
        return 0;
    }
    // Clamp to handle floating point errors
    const cosAngle = Math.max(-1, Math.min(1, dot / (magA * magB)));
    return Math.acos(cosAngle);
}
/**
 * Project vector a onto vector b
 */
export function project2(a, b) {
    const bMagSq = magnitudeSquared2(b);
    if (bMagSq === 0) {
        return VEC2_ZERO;
    }
    const scalar = dot2(a, b) / bMagSq;
    return scale2(b, scalar);
}
/**
 * Reflect vector a off a surface with normal n
 */
export function reflect2(a, n) {
    const normalizedN = normalize2(n);
    const dotAN = dot2(a, normalizedN);
    return sub2(a, scale2(normalizedN, 2 * dotAN));
}
/**
 * Clamp vector magnitude
 */
export function clampMagnitude2(v, maxMag) {
    const magSq = magnitudeSquared2(v);
    if (magSq > maxMag * maxMag) {
        return scale2(normalize2(v), maxMag);
    }
    return v;
}
/**
 * Create vector from polar coordinates
 */
export function fromPolar(r, theta) {
    return {
        x: r * Math.cos(theta),
        y: r * Math.sin(theta),
    };
}
// ============================================================================
// 3D VECTOR OPERATIONS
// ============================================================================
/**
 * Create a new 3D vector
 */
export function vec3(x, y, z) {
    return { x, y, z };
}
/**
 * Zero vector
 */
export const VEC3_ZERO = { x: 0, y: 0, z: 0 };
/**
 * Unit vectors
 */
export const VEC3_UNIT_X = { x: 1, y: 0, z: 0 };
export const VEC3_UNIT_Y = { x: 0, y: 1, z: 0 };
export const VEC3_UNIT_Z = { x: 0, y: 0, z: 1 };
/**
 * Add two 3D vectors
 */
export function add3(a, b) {
    return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}
/**
 * Subtract 3D vectors
 */
export function sub3(a, b) {
    return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}
/**
 * Multiply 3D vector by scalar
 */
export function scale3(v, s) {
    return { x: v.x * s, y: v.y * s, z: v.z * s };
}
/**
 * Dot product for 3D
 */
export function dot3(a, b) {
    return a.x * b.x + a.y * b.y + a.z * b.z;
}
/**
 * Cross product for 3D
 */
export function cross3(a, b) {
    return {
        x: a.y * b.z - a.z * b.y,
        y: a.z * b.x - a.x * b.z,
        z: a.x * b.y - a.y * b.x,
    };
}
/**
 * Magnitude of 3D vector
 */
export function magnitude3(v) {
    return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
}
/**
 * Normalize 3D vector
 */
export function normalize3(v) {
    const mag = magnitude3(v);
    if (mag === 0) {
        return VEC3_ZERO;
    }
    return { x: v.x / mag, y: v.y / mag, z: v.z / mag };
}
//# sourceMappingURL=vector.js.map